
/* ====================================================================================================================
   CDC SHOPPABLE VIDEO
   Kopstorm custom section for Celeste D'or Curve (Celovene theme, Enterprise/CleanCanvas architecture)

   Defines three custom elements:
     - <cdc-sv-quick-add> : split add-to-cart button with a variant dropdown. Uses the theme's native cart flow:
                            POST to cart/add.js with the page-header and cart-drawer section ids bundled, pushes the
                            returned HTML through updateFromCartChange(), opens .js-cart-drawer, and dispatches
                            on:cart:add / on:cart:change so the rest of the theme (and apps) react as usual.
     - <cdc-sv-grid>      : the grid wrapper. Wires card clicks to the reels player and drives in-viewport
                            autoplay of muted grid videos with an IntersectionObserver.
     - <cdc-sv-reels>     : the floating vertical (TikTok-style) player with up/down navigation, swipe,
                            keyboard, mute and per-slide product card.
   ==================================================================================================================== */

(function () {
  'use strict';

  function ensureVideoSrc(video) {
    if (!video || video.dataset.cdcSrcReady === '1') return;
    var attached = false;
    video.querySelectorAll('source[data-src]').forEach(function (source) {
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.getAttribute('data-src'));
        attached = true;
      }
    });
    if (attached) {
      try { video.load(); } catch (e) { /* no-op */ }
    }
    video.dataset.cdcSrcReady = '1';
  }

  function playVideo(video) {
    if (!video) return;
    ensureVideoSrc(video);
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
    return p;
  }

  /* ------------------------------------------------------------------------------------------------------------------
     Shared cart helper. Mirrors the theme's ProductForm.handleSubmit sequence so the cart drawer, header cart
     count and added notification all react exactly as they do for a native add to cart.
     ------------------------------------------------------------------------------------------------------------------ */
  async function addVariantToCart(sourceEl, variantId, quantity) {
    var cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
    var sectionsToUpdate = ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return el; });
    var sectionIds = sectionsToUpdate.map(function (el) { return el.dataset.sectionId; });

    var body = new FormData();
    body.set('id', variantId);
    body.set('quantity', quantity || 1);
    body.set('sections', sectionIds.join(','));
    body.set('sections_url', window.location.pathname);

    var response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    });

    var data = await response.json();

    if (!response.ok) {
      sourceEl.dispatchEvent(
        new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: (data && (data.description || data.message)) || 'Add to cart failed' }
        })
      );
      // Cart may change despite error.
      sourceEl.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      throw new Error((data && (data.description || data.message)) || 'Add to cart failed');
    }

    var afterAddToCart = (window.theme && theme.settings && theme.settings.afterAddToCart) || 'drawer';

    if (document.querySelector('.template-cart')) {
      // Cart page: refresh the cart form in place.
      var cartForm = document.querySelector('cart-form');
      if (cartForm && cartForm.enableAjaxUpdate) {
        cartForm.refresh();
      }
    } else if (afterAddToCart === 'page') {
      window.location.href = (window.theme && theme.routes && theme.routes.cart) || '/cart';
      return data;
    } else {
      if (data.sections && sectionsToUpdate.length && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach(function (el) {
          var html = data.sections[el.dataset.sectionId];
          if (html && typeof el.updateFromCartChange === 'function') {
            el.updateFromCartChange(html);
          }
        });
      } else {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }

      if (afterAddToCart === 'notification') {
        var tpl = document.getElementById('AddedNotification');
        if (tpl) {
          var notification = tpl.content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = data.product_title;
          var notificationContainer = document.querySelector('.pageheader--sticky') || document.querySelector('body');
          notificationContainer.appendChild(notification);
        }
      } else {
        var cartDrawer = document.querySelector('.js-cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
    }

    sourceEl.dispatchEvent(
      new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: data.variant_id || variantId } })
    );

    return data;
  }

  /* ------------------------------------------------------------------------------------------------------------------
     <cdc-sv-quick-add>
     ------------------------------------------------------------------------------------------------------------------ */
  var CdcSvQuickAdd = class extends HTMLElement {
    connectedCallback() {
      this.addButton = this.querySelector('[data-sv-add-default]');
      this.toggle = this.querySelector('[data-sv-toggle]');
      this.menu = this.querySelector('[data-sv-menu]');

      this._onClick = this._onClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
    }

    _onClick(event) {
      var addTrigger = event.target.closest('[data-sv-add]');
      if (addTrigger && !addTrigger.disabled) {
        event.preventDefault();
        event.stopPropagation();
        this._add(addTrigger);
        return;
      }

      if (this.toggle && event.target.closest('[data-sv-toggle]')) {
        event.preventDefault();
        event.stopPropagation();
        this.expanded ? this._close() : this._open();
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && this.expanded) {
        event.stopPropagation();
        this._close();
        if (this.toggle) this.toggle.focus();
      }
    }

    _onDocClick(event) {
      if (!this.contains(event.target)) this._close();
    }

    get expanded() {
      return this.getAttribute('aria-expanded') === 'true';
    }

    _open() {
      if (!this.menu) return;
      this.menu.hidden = false;
      this.setAttribute('aria-expanded', 'true');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      // Defer so this same click does not immediately close it.
      setTimeout(function () {
        document.addEventListener('click', this._onDocClick);
      }.bind(this), 0);
    }

    _close() {
      if (!this.menu) return;
      this.menu.hidden = true;
      this.setAttribute('aria-expanded', 'false');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this._onDocClick);
    }

    async _add(trigger) {
      var variantId = trigger.getAttribute('data-variant-id');
      if (!variantId) return;

      var label = this.addButton ? this.addButton.querySelector('.cdc-svqa__add-label') : null;
      var spinner = this.addButton ? this.addButton.querySelector('.cdc-svqa__spinner') : null;
      var originalText = label ? label.textContent : '';

      this._close();

      if (this.addButton) {
        this.addButton.setAttribute('aria-busy', 'true');
        if (spinner) spinner.hidden = false;
      }

      try {
        await addVariantToCart(this, variantId, 1);
        if (label) {
          label.textContent = '\u2713 Added';
          var btn = this.addButton;
          setTimeout(function () {
            if (label) label.textContent = originalText;
            if (btn) btn.removeAttribute('aria-busy');
          }, 1400);
        }
      } catch (e) {
        if (label) label.textContent = originalText;
        if (this.addButton) this.addButton.removeAttribute('aria-busy');
      } finally {
        if (spinner) spinner.hidden = true;
      }
    }
  };

  if (!window.customElements.get('cdc-sv-quick-add')) {
    window.customElements.define('cdc-sv-quick-add', CdcSvQuickAdd);
  }

  /* ------------------------------------------------------------------------------------------------------------------
     <cdc-sv-reels> : floating vertical player
     ------------------------------------------------------------------------------------------------------------------ */
  var CdcSvReels = class extends HTMLElement {
    connectedCallback() {
      this.feed = this.querySelector('[data-sv-feed]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-sv-slide]'));
      this.prevBtn = this.querySelector('[data-sv-prev]');
      this.nextBtn = this.querySelector('[data-sv-next]');
      this.muteBtn = this.querySelector('[data-sv-mute]');
      this.closeBtn = this.querySelector('[data-sv-close]');
      this.viewport = this.querySelector('[data-sv-viewport]');

      this.index = 0;
      this.muted = true;
      this._wheelLock = false;
      this._abort = new AbortController();
      var signal = this._abort.signal;

      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { this.go(this.index - 1); }.bind(this), { signal: signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { this.go(this.index + 1); }.bind(this), { signal: signal });
      if (this.muteBtn) this.muteBtn.addEventListener('click', this._toggleMute.bind(this), { signal: signal });
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.close.bind(this), { signal: signal });

      this.addEventListener('keydown', this._onKeydown.bind(this), { signal: signal });

      if (this.viewport) {
        this.viewport.addEventListener('wheel', this._onWheel.bind(this), { passive: false, signal: signal });
        this.viewport.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true, signal: signal });
        this.viewport.addEventListener('touchend', this._onTouchEnd.bind(this), { passive: true, signal: signal });
        this.viewport.addEventListener('click', this._onTapToggle.bind(this), { signal: signal });
      }
    }

    disconnectedCallback() {
      this._abort.abort();
    }

    open(index) {
      if (!this._svHome) this._svHome = this.parentNode;
      if (this.parentNode !== document.body) document.body.appendChild(this);
      this.index = Math.max(0, Math.min(index || 0, this.slides.length - 1));
      this.setAttribute('open', '');
      this.setAttribute('data-paused', 'false');
      this._render(false);
      this.dispatchEvent(new CustomEvent('cdc-sv-reels:open', { bubbles: true }));
    }

    close() {
      this.removeAttribute('open');
      this.slides.forEach(this._pauseSlide, this);
      this.dispatchEvent(new CustomEvent('cdc-sv-reels:close', { bubbles: true }));
      if (this._svHome && this.parentNode === document.body) this._svHome.appendChild(this);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    go(index) {
      var n = this.slides.length;
      if (n < 1) return;
      if (index < 0) index = n - 1;
      else if (index > n - 1) index = 0;
      this.index = index;
      this._render(true);
    }

    _render(animate) {
      var i = this.index;
      if (this.feed) {
        if (!animate) {
          var prevTransition = this.feed.style.transition;
          this.feed.style.transition = 'none';
          this.feed.style.transform = 'translateY(' + (-i * 100) + '%)';
          // Force reflow then restore transition.
          void this.feed.offsetHeight;
          this.feed.style.transition = prevTransition || '';
        } else {
          this.feed.style.transform = 'translateY(' + (-i * 100) + '%)';
        }
      }

      this.slides.forEach(function (slide, idx) {
        var active = idx === i;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          this._activateSlide(slide);
        } else {
          this._pauseSlide(slide);
        }
      }, this);

      if (this.prevBtn) this.prevBtn.disabled = this.slides.length < 2;
      if (this.nextBtn) this.nextBtn.disabled = this.slides.length < 2;

      // Still-image slides have no <video> and no embed: flag them so CSS can hide the
      // mute button and tap-to-pause glyph, and never leave the paused affordance showing.
      var activeSlide = this.slides[i];
      var isImage = activeSlide && !activeSlide.querySelector('video') && !activeSlide.querySelector('[data-sv-embed]');
      this.setAttribute('data-active-image', isImage ? 'true' : 'false');
      if (isImage) this.setAttribute('data-paused', 'false');
    }

    _activateSlide(slide) {
      // Native video
      var video = slide.querySelector('video');
      if (video) {
        this.setAttribute('data-paused', 'false');
        video.muted = this.muted;
        video.loop = true;
        video.playsInline = true;
        var p = playVideo(video);
        if (p && typeof p.catch === 'function') {
          p.catch(function () {
            video.muted = true;
            playVideo(video);
          });
        }
        return;
      }

      // Embed (YouTube / Vimeo / Instagram) lazy-injected from a template the first time the slide is shown.
      var holder = slide.querySelector('[data-sv-embed]');
      if (holder && !holder.dataset.loaded) {
        var tpl = holder.querySelector('template');
        if (tpl) {
          holder.appendChild(tpl.content.cloneNode(true));
          holder.dataset.loaded = 'true';
          if (holder.dataset.svEmbed === 'instagram') this._loadInstagram();
        }
      }
    }

    _pauseSlide(slide) {
      var video = slide.querySelector('video');
      if (video && !video.paused) video.pause();
    }

    _onTapToggle(event) {
      // Ignore taps on the controls, product card, links or embeds; only toggle on the video surface.
      if (
        event.target.closest('.cdc-svr__btn') ||
        event.target.closest('.cdc-svr__product') ||
        event.target.closest('cdc-sv-quick-add') ||
        event.target.closest('a') ||
        event.target.closest('[data-sv-embed]')
      ) {
        return;
      }
      var active = this.slides[this.index];
      var video = active ? active.querySelector('video') : null;
      if (!video) return;
      if (video.paused) {
        playVideo(video);
        this.setAttribute('data-paused', 'false');
      } else {
        video.pause();
        this.setAttribute('data-paused', 'true');
      }
    }

    _toggleMute() {
      this.muted = !this.muted;
      this.setAttribute('data-muted', this.muted ? 'true' : 'false');
      var active = this.slides[this.index];
      var video = active ? active.querySelector('video') : null;
      if (video) video.muted = this.muted;
      this._renderMuteIcon();
    }

    _renderMuteIcon() {
      if (!this.muteBtn) return;
      var on = this.muteBtn.querySelector('[data-icon-unmuted]');
      var off = this.muteBtn.querySelector('[data-icon-muted]');
      if (on) on.hidden = this.muted;
      if (off) off.hidden = !this.muted;
      this.muteBtn.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
    }

    _loadInstagram() {
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
        return;
      }
      if (document.getElementById('cdc-sv-instagram-embed-sdk')) return;
      var s = document.createElement('script');
      s.id = 'cdc-sv-instagram-embed-sdk';
      s.async = true;
      s.src = 'https://www.instagram.com/embed.js';
      document.body.appendChild(s);
    }

    _onKeydown(event) {
      if (!this.isOpen) return;
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          this.go(this.index + 1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          this.go(this.index - 1);
          break;
        case 'm':
        case 'M':
          this._toggleMute();
          break;
      }
    }

    _onWheel(event) {
      // Ignore wheel that originates inside the scrollable variant menu.
      if (event.target.closest('.cdc-svqa__menu')) return;
      event.preventDefault();
      if (this._wheelLock) return;
      if (Math.abs(event.deltaY) < 12) return;
      this._wheelLock = true;
      this.go(this.index + (event.deltaY > 0 ? 1 : -1));
      setTimeout(function () { this._wheelLock = false; }.bind(this), 520);
    }

    _onTouchStart(event) {
      this._touchY = event.changedTouches[0].clientY;
      this._touchX = event.changedTouches[0].clientX;
    }

    _onTouchEnd(event) {
      if (event.target.closest('.cdc-svr__product') || event.target.closest('.cdc-svqa__menu')) return;
      var dy = event.changedTouches[0].clientY - (this._touchY || 0);
      var dx = event.changedTouches[0].clientX - (this._touchX || 0);
      if (Math.abs(dy) < 45 || Math.abs(dy) < Math.abs(dx)) return;
      this.go(this.index + (dy < 0 ? 1 : -1));
    }
  };

  if (!window.customElements.get('cdc-sv-reels')) {
    window.customElements.define('cdc-sv-reels', CdcSvReels);
  }

  /* ------------------------------------------------------------------------------------------------------------------
     <cdc-sv-grid> : grid wrapper, opens the reels player on card click and autoplays in-viewport grid videos.
     ------------------------------------------------------------------------------------------------------------------ */
  var CdcSvGrid = class extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      var reelsId = this.getAttribute('data-reels');
      this.reels = reelsId ? document.getElementById(reelsId) : null;

      this._pausedByModal = [];
      this._modalOpen = false;
      if (this.reels) {
        this.reels.addEventListener('cdc-sv-reels:open', this._onModalOpen.bind(this));
        this.reels.addEventListener('cdc-sv-reels:close', this._onModalClose.bind(this));
      }

      // In-viewport autoplay for muted grid videos (replaces Impact's <video-media> element).
      if (this.hasAttribute('data-autoplay') && 'IntersectionObserver' in window) {
        this._io = new IntersectionObserver(this._onIntersect.bind(this), { threshold: 0.35 });
        this.querySelectorAll('video[data-sv-autoplay]').forEach(function (video) {
          this._io.observe(video);
        }, this);
      }

      // Mobile / tablet / desktop-slider carousel: arrows + progress bar driven by the scroll position.
      this.track = this.querySelector('[data-sv-track]');
      this.prevCard = this.querySelector('[data-sv-prev-card]');
      this.nextCard = this.querySelector('[data-sv-next-card]');
      this.progressFill = this.querySelector('[data-sv-progress-fill]');
      this._updateProgress = this._updateProgress.bind(this);

      if (this.prevCard) this.prevCard.addEventListener('click', this._scrollByPage.bind(this, -1));
      if (this.nextCard) this.nextCard.addEventListener('click', this._scrollByPage.bind(this, 1));

      if (this.track) {
        this.track.addEventListener('scroll', this._updateProgress, { passive: true });
        requestAnimationFrame(this._updateProgress);
        if (window.ResizeObserver) {
          this._ro = new ResizeObserver(this._updateProgress);
          this._ro.observe(this.track);
        } else {
          window.addEventListener('resize', this._updateProgress);
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
    }

    _onIntersect(entries) {
      // While the player is open, grid videos stay paused; _onModalClose resumes them.
      if (this._modalOpen) return;
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          video.muted = true;
          playVideo(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    _cards() {
      if (!this.track) return [];
      var cards = [];
      for (var i = 0; i < this.track.children.length; i++) {
        if (this.track.children[i].nodeType === 1) cards.push(this.track.children[i]);
      }
      return cards;
    }

    _maxScroll() {
      if (!this.track) return 0;
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    _offsetOf(card) {
      var origin = this.track.getBoundingClientRect().left;
      return card.getBoundingClientRect().left - origin + this.track.scrollLeft;
    }

    _currentIndex() {
      var cards = this._cards();
      if (!cards.length) return 0;
      var origin = this.track.getBoundingClientRect().left;
      var current = 0;
      var best = Infinity;
      for (var j = 0; j < cards.length; j++) {
        var dist = Math.abs(cards[j].getBoundingClientRect().left - origin);
        if (dist < best) {
          best = dist;
          current = j;
        }
      }
      return current;
    }

    _lastIndex() {
      var cards = this._cards();
      var max = this._maxScroll();
      if (!cards.length) return 0;
      var last = 0;
      for (var i = 0; i < cards.length; i++) {
        if (this._offsetOf(cards[i]) <= max + 0.5) last = i;
      }
      return last;
    }

    _scrollToLeft(left, behavior) {
      var track = this.track;
      var max = this._maxScroll();
      if (left < 0) left = 0;
      if (left > max) left = max;
      left = Math.round(left);
      var snap = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      var restored = false;
      var restore = function () {
        if (restored) return;
        restored = true;
        track.style.scrollSnapType = snap;
        track.removeEventListener('scrollend', restore);
      };
      track.addEventListener('scrollend', restore);
      window.setTimeout(restore, 450);
      if (typeof track.scrollTo === 'function') track.scrollTo({ left: left, behavior: behavior || 'smooth' });
      else { track.scrollLeft = left; restore(); }
    }

    _scrollByPage(dir) {
      if (!this.track) return;
      var cards = this._cards();
      if (!cards.length) return;
      var last = this._lastIndex();
      var current = this._currentIndex();
      if (current > last) current = last;
      var next = current + dir;
      if (next < 0) next = last;
      else if (next > last) next = 0;
      var wrapping = (dir > 0 && next < current) || (dir < 0 && next > current);
      this._scrollToLeft(this._offsetOf(cards[next]), wrapping ? 'auto' : 'smooth');
    }

    _updateProgress() {
      if (!this.track) return;
      var max = this._maxScroll();
      if (max <= 1) {
        if (this.progressFill) {
          this.progressFill.style.insetInlineStart = '0%';
          this.progressFill.style.width = '100%';
        }
        if (this.prevCard) this.prevCard.disabled = true;
        if (this.nextCard) this.nextCard.disabled = true;
        return;
      }
      var visible = this.track.clientWidth / this.track.scrollWidth;
      if (!(visible > 0) || visible > 1) visible = 1;
      var start = this.track.scrollLeft / this.track.scrollWidth;
      if (start < 0) start = 0;
      if (this.progressFill) {
        this.progressFill.style.width = (visible * 100) + '%';
        this.progressFill.style.insetInlineStart = (start * 100) + '%';
      }
      if (this.prevCard) this.prevCard.disabled = false;
      if (this.nextCard) this.nextCard.disabled = false;
    }

    _onModalOpen() {
      this._modalOpen = true;
      this._pausedByModal = [];
      var videos = this.querySelectorAll('.cdc-sv__media video');
      for (var i = 0; i < videos.length; i++) {
        if (!videos[i].paused) {
          videos[i].pause();
          this._pausedByModal.push(videos[i]);
        }
      }
    }

    _onModalClose() {
      this._modalOpen = false;
      for (var i = 0; i < this._pausedByModal.length; i++) {
        var v = this._pausedByModal[i];
        if (v && v.isConnected) playVideo(v);
      }
      this._pausedByModal = [];
    }

    _isMobile() {
      return window.matchMedia('(max-width: 999.98px)').matches;
    }

    _playInline(opener) {
      var video = opener.querySelector('video');
      if (!video) return;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.playsInline = true;
      video.muted = true;
      if (!video.dataset.cdcInlineBound) {
        video.dataset.cdcInlineBound = '1';
        video.addEventListener('webkitbeginfullscreen', function (event) {
          if (event && event.preventDefault) event.preventDefault();
          if (video.webkitExitFullscreen) video.webkitExitFullscreen();
        });
      }
      this.querySelectorAll('.cdc-sv__media.is-playing').forEach(function (el) {
        if (el === opener) return;
        el.classList.remove('is-playing');
        var other = el.querySelector('video');
        if (other) other.pause();
      });
      if (opener.classList.contains('is-playing') && !video.paused) {
        video.pause();
        opener.classList.remove('is-playing');
        return;
      }
      opener.classList.add('is-playing');
      playVideo(video);
    }

    _onClick(event) {
      var opener = event.target.closest('[data-sv-open]');
      if (!opener) return;
      if (event.target.closest('cdc-sv-quick-add') || event.target.closest('a')) return;
      event.preventDefault();
      if (this._isMobile()) {
        this._playInline(opener);
        return;
      }
      var index = parseInt(opener.getAttribute('data-sv-open'), 10) || 0;
      if (this.reels) this.reels.open(index);
    }
  };

  if (!window.customElements.get('cdc-sv-grid')) {
    window.customElements.define('cdc-sv-grid', CdcSvGrid);
  }
})();

